import React, { useContext } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  NonCopyableMarkdown,
  SectionWrapper,
  WideOptionsContainer,
  WideOption,
  Label,
  NonCopyableMarkdownOption,
} from '../common';
import { useDndScrollLock } from '../../../hooks/assessment-test/use-dnd-scroll-lock';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';

export default function RankingSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'ranking' }
  >;
  stepId: string;
}) {
  const { answer, content } = section;
  const [, dispatch] = useContext(AssessmentTestContext);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const setAnswer = React.useCallback(
    (value: Array<{ id: string; content: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_RANKING',
        payload: {
          stepId,
          sectionId: section.id,
          answer: value,
        },
      });
    },
    [stepId, section.id, dispatch]
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = answer.findIndex((i) => i.id === active.id);
      const newIndex = answer.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      setAnswer(arrayMove(answer, oldIndex, newIndex));
    },
    [answer, setAnswer]
  );

  const { onDragStart, wrapOnDragEnd } = useDndScrollLock();
  const handleDragEndWrapped = React.useMemo(
    () => wrapOnDragEnd<DragEndEvent>(handleDragEnd),
    [wrapOnDragEnd, handleDragEnd]
  );

  return (
    <SectionWrapper>
      <NonCopyableMarkdown md={content} />
      <DndContext
        sensors={sensors}
        // onDragStart & onDragEnd are used to prevent a bug that happens when you drag the element outside of the container as it jumped to the bottom of the page
        onDragStart={onDragStart}
        onDragEnd={handleDragEndWrapped}
        modifiers={[
          restrictToVerticalAxis,
          restrictToFirstScrollableAncestor,
          restrictToParentElement,
        ]}
      >
        <SortableContext
          items={answer.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <WideOptionsContainer>
            {answer.map((item, index) => (
              <SortableItem
                key={item.id}
                id={item.id}
                index={index}
                content={item.content}
              />
            ))}
          </WideOptionsContainer>
        </SortableContext>
      </DndContext>
      <Label>Drag and drop to reorder. </Label>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}

function SortableItem({
  id,
  // index,
  content,
}: {
  id: string;
  index: number;
  content: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    userSelect: 'none',
    cursor: 'grab',
    zIndex: isDragging ? 2 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <WideOption isSelected={false}>
        <div>
          <NonCopyableMarkdownOption md={content} />
        </div>
      </WideOption>
    </div>
  );
}
